import React, { useState, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';

interface AuthenticatedImageProps {
  src: string;
  alt: string;
  height?: number | string;
  onClick?: () => void;
  sx?: any;
}

const AuthenticatedImage: React.FC<AuthenticatedImageProps> = ({ src, alt, height, onClick, sx }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [imageUrl, setImageUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchImage = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(src, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to load image');
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setImageUrl(url);
        setLoading(false);
      } catch (err) {
        console.error('Error loading image:', err);
        setError(true);
        setLoading(false);
      }
    };

    fetchImage();

    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [src, getAccessTokenSilently]);

  if (loading) {
    return (
      <Box
        sx={{
          height: height || 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.200',
          ...sx,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error || !imageUrl) {
    return null;
  }

  return (
    <Box
      component="img"
      src={imageUrl}
      alt={alt}
      onClick={onClick}
      sx={{
        width: '100%',
        height: height || 200,
        objectFit: 'cover',
        ...sx,
      }}
    />
  );
};

export default AuthenticatedImage;
